import _ from "lodash";
import React, { useEffect, useState, useContext, useRef } from "react";
// react-bootstrap components
import { Card, Container, Row, Col, Button } from "react-bootstrap";
import { QRCodeCanvas } from "qrcode.react";
import { apiService } from "../../../service/ApiService";
import { notificationService } from "../../../service/NotificationService";
import StoreContext from "contexts/StoreContext";
import HippoTable from "components/ReactTable/HipposReactTable";
import StatusColumn from "components/Custom/StatusColumn";
import ColorColumn from "components/Custom/ColorColumn";
import TableQrModal from "components/Modals/TableQrModal";
import { getTableQrUrl, getTableQrFileName, downloadCanvasAsPng } from "utils/tableQr";
import { ClipLoader } from "react-spinners";

import { useTranslation } from "react-i18next";

const QR_SIZE = 512;

function TableListPage() {
  const { t } = useTranslation();

  const [data, setData] = useState([]);
  const { storeId } = useContext(StoreContext);
  const [showLoader, setShowLoader] = useState(true);
  const [qrTable, setQrTable] = useState(null);
  const hiddenQrsRef = useRef(null);

  const columns = [
    {
      Header: "ID",
      accessor: "id",
      maxWidth: 70,
      minWidth: 40,
      width: 60,
    },
    {
      Header: t("tables.headers.designation"),
      accessor: "name",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.region"),
      accessor: "region",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.position"),
      accessor: "order",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.color"),
      accessor: "colorComponent",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.status"),
      accessor: "status",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.actions"),
      accessor: "actions",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
  ];

  const fetchData = async () => {
    setShowLoader(true);
    try {
      const dataResponse = await apiService.get(`api/v1/tables/store/${storeId}`);
      setData(attachCustom(dataResponse));
    } catch (error) {
      console.error("Error fetching data:", error);
      notificationService.notifyWarn(error.message);
    } finally {
      setShowLoader(false);
    }
  };

  const attachCustom = (data) => {
    const enhanced = data.map((el) => {
      const table = { id: el.id, name: el.name, uuid: el.uuid, regionName: el.region ? el.region.name : "" };
      el.status = <StatusColumn value={el.deleted} />;
      el.colorComponent = <ColorColumn color={el.color} />;
      el.actions = (
        <div className="float-right">
          <Button
            onClick={() => setQrTable(table)}
            variant="info"
            size="md"
            className="text-info btn-link"
            style={{ padding: "2px 5px" }}
            title={el.uuid ? t("common.labels.qrCode") : t("messages.tableHasNoQr")}
            disabled={!el.uuid}
          >
            <i className="fa fa-qrcode" />
          </Button>
          <Button
            onClick={() => downloadQr(table)}
            variant="success"
            size="md"
            className="text-success btn-link"
            style={{ padding: "2px 5px" }}
            title={el.uuid ? t("common.actions.downloadQr") : t("messages.tableHasNoQr")}
            disabled={!el.uuid}
          >
            <i className="fa fa-download" />
          </Button>
          <Button
            onClick={() => copyQrUrl(table)}
            variant="primary"
            size="md"
            className="text-primary btn-link"
            style={{ padding: "2px 5px" }}
            title={el.uuid ? t("common.actions.copyTableUrl") : t("messages.tableHasNoQr")}
            disabled={!el.uuid}
          >
            <i className="fa fa-copy" />
          </Button>
        </div>
      );
      el.region = el.region ? el.region.name : "";
      return el;
    });
    return _.sortBy(enhanced, ["order", "id"]);
  };

  const qrTables = data.filter((el) => el.uuid);

  const downloadQr = (table) => {
    const canvas = hiddenQrsRef.current && hiddenQrsRef.current.querySelector(`canvas[data-table-id="${table.id}"]`);
    if (canvas) downloadCanvasAsPng(canvas, getTableQrFileName(table, "png"));
  };

  const copyQrUrl = (table) => {
    navigator.clipboard
      .writeText(getTableQrUrl(table.uuid))
      .then(() => notificationService.notifySuccess(t("messages.tableUrlCopiedToClipboard")))
      .catch((error) => notificationService.notifyError(error.message));
  };

  useEffect(() => {
    if (!storeId) return;
    fetchData();
  }, [storeId]);

  return (
    <>
      <TableQrModal table={qrTable} show={!!qrTable} handleClose={() => setQrTable(null)} />
      {/* Off-screen codes the row download button saves */}
      <div ref={hiddenQrsRef} style={{ display: "none" }}>
        {qrTables.map((table) => (
          <QRCodeCanvas key={table.id} data-table-id={table.id} value={getTableQrUrl(table.uuid)} size={QR_SIZE} includeMargin={true} />
        ))}
      </div>
      <Container fluid>
        <Row>
          <Col md="12">
            <Card>
              <Card.Body>
                <Row>
                  <Col>
                    {showLoader ? (
                      <div style={{ textAlign: "center", marginTop: "5vh", marginBottom: "5vh" }}>
                        <ClipLoader size={50} color={"#ff6fbc"} />
                      </div>
                    ) : (
                      <HippoTable data={data} columns={columns} className="-striped -highlight primary-pagination" />
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default TableListPage;
